import React, { useState } from 'react';

import { Area, CartesianGrid, ComposedChart, Line, ReferenceArea, ResponsiveContainer, XAxis, YAxis } from 'recharts';

import { Box, Stack, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';

import ReportChartTooltip from '~/components/Common/ReportChartTooltip';

import { UI, cardSx, value } from './operationalUi';

export interface TrajectoryPoint {
  day: number;
  actual: number | null;
  q1: number | null;
  median: number | null;
  q3: number | null;
  peersRemaining: number;
  bandAvailable?: boolean;
}
interface Props {
  points: TrajectoryPoint[];
  bandEnd: number | null;
  measure: string;
  onMeasure: (measure: string) => void;
  market: string;
  action?: React.ReactNode;
}
export default function TrajectoryChart({ points, bandEnd, measure, onMeasure, market, action }: Props) {
  const [readout, setReadout] = useState<HTMLElement | null>(null);
  const [chartEngaged, setChartEngaged] = useState(false);
  const rows = points.map((p) => ({
    ...p,
    band: p.bandAvailable === false || (!p.median && p.median !== 0) ? null : [p.q1, p.q3],
    median: p.bandAvailable === false ? null : p.median,
  }));
  const last = points[points.length - 1],
    bandLast = points.find((p) => p.day === bandEnd),
    unit = measure === 'margin' ? 'currency' : '';
  return (
    <Box sx={{ ...cardSx, display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0, overflowY: 'auto' }}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        gap={1}
        flexWrap="wrap"
        sx={{ flexShrink: 0 }}
      >
        <Stack direction="row" spacing={1} alignItems="baseline">
          <Typography sx={{ fontSize: 13, color: UI.teal, fontWeight: 600, letterSpacing: 0.6 }}>
            SINCE LAUNCH
          </Typography>
          <Typography sx={{ fontSize: 12, color: UI.muted }}>Is it picking up?</Typography>
        </Stack>
        {action}
        <ToggleButtonGroup
          exclusive
          size="small"
          value={measure}
          onChange={(_, v) => v && onMeasure(v)}
          aria-label="Trajectory measure"
        >
          <ToggleButton value="packages">Packages</ToggleButton>
          <ToggleButton value="margin">Margin</ToggleButton>
        </ToggleButtonGroup>
      </Stack>
      <Typography sx={{ fontSize: 12, color: UI.muted, mt: 1, flexShrink: 0 }}>
        ━ This campaign · ▰ Middle half of peers · ┄ Peer median
      </Typography>
      <Box
        onMouseEnter={() => setChartEngaged(true)}
        onMouseLeave={() => setChartEngaged(false)}
        onFocusCapture={() => setChartEngaged(true)}
        onBlurCapture={(event) => {
          if (!event.currentTarget.contains(event.relatedTarget)) setChartEngaged(false);
        }}
        sx={{ flex: 1, minHeight: 100, mt: 1 }}
      >
        {points.length ? (
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={rows} margin={{ top: 12, right: 15, bottom: 12, left: 8 }}>
              <defs>
                <pattern id="flash-no-peer-band" patternUnits="userSpaceOnUse" width="7" height="7">
                  <path d="M-1,1 l2,-2 M0,7 l7,-7 M6,8 l2,-2" stroke={UI.line} strokeWidth="1" />
                </pattern>
              </defs>
              <CartesianGrid vertical={false} stroke={UI.line} />
              <XAxis
                dataKey="day"
                type="number"
                domain={['dataMin', 'dataMax']}
                tick={{ fontSize: 12, fill: UI.muted }}
                axisLine={false}
                tickLine={false}
                label={{
                  value: 'Days since launch',
                  position: 'insideBottom',
                  offset: -8,
                  fontSize: 12,
                  fill: UI.muted,
                }}
              />
              <YAxis
                tick={{ fontSize: 12, fill: UI.muted }}
                axisLine={false}
                tickLine={false}
                width={58}
                tickFormatter={(v) =>
                  measure === 'margin' ? (Math.abs(v) >= 1000 ? 'A$' + Math.round(v / 1000) + 'k' : 'A$' + v) : value(v)
                }
              />
              <ReportChartTooltip
                portal={readout}
                active={readout && chartEngaged ? undefined : false}
                position={{ x: 0, y: 0 }}
                isAnimationActive={false}
                wrapperStyle={{ position: 'absolute', top: 0, left: 0, width: '100%', pointerEvents: 'none' }}
                content={({ active, payload, label }) => {
                  if (!active || !payload?.length) return null;
                  const point = payload[0].payload as TrajectoryPoint;
                  return (
                    <Box sx={{ bgcolor: UI.paper, minHeight: 48, py: 0.5 }}>
                      <Typography sx={{ fontSize: 13, fontWeight: 600, color: UI.teal }}>
                        Day {label} · This campaign {value(point.actual, unit)}
                      </Typography>
                      <Typography sx={{ fontSize: 12, color: UI.muted }}>
                        Peer median {point.bandAvailable === false ? 'No band' : value(point.median, unit)}
                        {' · '}
                        {point.peersRemaining} peers remaining
                      </Typography>
                    </Box>
                  );
                }}
              />
              {bandEnd !== null && last?.day > bandEnd && (
                <ReferenceArea
                  x1={bandEnd}
                  x2={last.day}
                  fill="url(#flash-no-peer-band)"
                  label={{
                    value: `No peer band after day ${bandEnd}`,
                    position: 'insideTopRight',
                    fontSize: 12,
                    fill: UI.muted,
                  }}
                />
              )}
              <Area
                dataKey="band"
                name="Middle half of peers"
                fill={UI.teal}
                fillOpacity={0.12}
                stroke="none"
                connectNulls={false}
                isAnimationActive={false}
              />
              <Line
                dataKey="median"
                name="Peer median"
                stroke={UI.muted}
                strokeWidth={1.5}
                strokeDasharray="5 4"
                dot={false}
                connectNulls={false}
                isAnimationActive={false}
              />
              <Line
                dataKey="actual"
                name="This campaign"
                stroke={UI.teal}
                strokeWidth={2.5}
                dot={false}
                isAnimationActive={false}
              />
              <Line dataKey="peersRemaining" name="Peers remaining" hide />
            </ComposedChart>
          </ResponsiveContainer>
        ) : (
          <Typography sx={{ color: UI.muted, p: 3 }}>No launch-age facts are available under this scope.</Typography>
        )}
      </Box>
      <Box
        ref={setReadout}
        data-testid="flash-trajectory-readout"
        sx={{ position: 'relative', minHeight: 48, flexShrink: 0 }}
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          spacing={1}
          sx={{ py: 1, visibility: chartEngaged ? 'hidden' : 'visible' }}
        >
          <Typography sx={{ fontSize: 13, color: UI.teal, fontWeight: 600 }}>
            This campaign {value(last?.actual, unit)} at day {last?.day || '—'}
          </Typography>
          <Typography sx={{ fontSize: 12, color: UI.muted }}>
            Peer median {value(bandLast?.median, unit)} · {bandLast?.peersRemaining || 0} peers at day {bandEnd || '—'}
          </Typography>
        </Stack>
      </Box>
      <Typography sx={{ fontSize: 12, color: UI.muted, flexShrink: 0, lineHeight: 1.3 }}>
        Recorded cumulative totals since launch. Missing daily records are not filled. The peer band changes with the
        peers still running. {market ? `Website-market ${market} trajectory and funnel.` : ''}
      </Typography>
    </Box>
  );
}
